import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/kit";
import GiveCard from "@/components/giving/GiveCard";
import GiveModal from "@/components/giving/GiveModal";
import { printReceipt } from "@/lib/receipt";
import { fmtDate } from "@/lib/dates";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const STATUS = { paid: "text-dark-olive", pending: "text-ink/60", failed: "text-maroon", refunded: "text-ink/60", simulated: "text-gold" };

// Signed-in give page: every Live campaign/fund + the person's own giving history.
export default function GivePage({ prefill }) {
  const [items, setItems] = useState(null);
  const [mine, setMine] = useState([]);
  const [give, setGive] = useState(null);

  const load = useCallback(async () => {
    const [c, m] = await Promise.all([api.get("/giving/campaigns"), api.get("/giving/my-gifts")]);
    setItems(c.data.items); setMine(m.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    try { await api.post(`/donations/${id}/resend-receipt`); toast.success("Receipt re-sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); }
  };

  return (
    <div data-testid="give-page" className="space-y-6">
      <div>
        <h1 className="font-zilla text-3xl font-semibold">Give to Rosemoor</h1>
        <p className="text-[15px] text-ink/70">Support a campaign or give where it's needed most. Receipts are emailed automatically.</p>
      </div>

      {items === null ? (
        <div className="text-ink/60 py-6">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-cream border border-ink/15 rounded-xl p-6 text-center text-ink/60" data-testid="give-empty">No campaigns are open right now.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">{items.map((c) => <GiveCard key={c.campaign_id} c={c} onGive={setGive} />)}</div>
      )}

      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-giving">
        <h2 className="font-zilla text-xl font-semibold mb-1">My giving</h2>
        <p className="text-[13px] text-ink/55 mb-3">Your past gifts. Only you can see these.</p>
        {mine.length === 0 ? (
          <div className="text-ink/50 text-[14px] py-4">You haven't given yet.</div>
        ) : (
          <div className="divide-y divide-ink/8">{mine.map((g) => (
            <div key={g.donation_id} data-testid={`mygift-${g.donation_id}`} className="flex items-center gap-3 py-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold">{g.campaign_title}</div>
                <div className="text-[12px] text-ink/55">{fmtDate(g.date)} · <span className={STATUS[g.status] || ""}>{g.status}</span>{g.simulated ? " · simulated" : ""}</div>
              </div>
              <div className="font-zilla text-lg font-semibold">{money(g.amount_cents)}</div>
              {g.status === "paid" && (
                <div className="flex gap-2">
                  <Button variant="secondary" data-testid={`resend-${g.donation_id}`} onClick={() => resend(g.donation_id)} className="px-3 py-2 min-h-[40px] text-[13px]">Resend receipt</Button>
                  <Button variant="secondary" data-testid={`print-${g.donation_id}`} onClick={() => printReceipt(g.donation_id)} className="px-3 py-2 min-h-[40px] text-[13px]">Print receipt</Button>
                </div>
              )}
            </div>
          ))}</div>
        )}
      </div>

      {give && <GiveModal campaign={give} prefill={prefill} onClose={() => setGive(null)} onDone={load} />}
    </div>
  );
}
